// Product analytics, session replay and error tracking (PostHog). The only file that knows
// posthog-js; it loads lazily once /api/me hands over the project key, so it never sits on the
// first-load path. Nothing starts under Global Privacy Control or a `no-tracking=1` cookie, and
// every call is a quiet no-op until (or unless) PostHog is running.
import type { CaptureResult, PostHog } from 'posthog-js';
import { useEffect, useRef } from 'react';

export type AnalyticsConfig = { posthogKey: string; posthogHost: string };
type Properties = Record<string, unknown>;
type Loader = () => Promise<{ default: PostHog }>;

/** The server reads this cookie on sign-up (services/attribution.ts). */
const ATTRIBUTION_COOKIE = 'strl_utm';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const ATTRIBUTION_DAYS = 30;

let client: PostHog | null = null;
let starting: Promise<boolean> | null = null;
/** Calls made while posthog-js is still loading, replayed in order once it is up. */
let pending: ((ph: PostHog) => void)[] = [];

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k === name) return rest.join('=');
  }
  return null;
}

/** False when the visitor asked not to be tracked: Global Privacy Control or no-tracking=1. */
export function trackingAllowed(): boolean {
  try {
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl)
      return false;
    return readCookie('no-tracking') !== '1';
  } catch {
    return false;
  }
}

function run(fn: (ph: PostHog) => void) {
  try {
    if (client) fn(client);
    else if (starting) pending.push(fn);
  } catch (err) {
    console.warn('[analytics] call failed', err);
  }
}

/** Starts PostHog once. Resolves true when it is running. Never rejects. */
export function startAnalytics(
  config: AnalyticsConfig | null | undefined,
  load: Loader = () => import('posthog-js'),
): Promise<boolean> {
  if (client) return Promise.resolve(true);
  if (starting) return starting;
  if (!config?.posthogKey || !trackingAllowed()) return Promise.resolve(false);
  starting = load()
    .then(({ default: posthog }) => {
      posthog.init(config.posthogKey, {
        api_host: config.posthogHost,
        person_profiles: 'identified_only',
        // The SPA's route changes are pushState: PostHog counts each as a $pageview.
        capture_pageview: 'history_change',
        capture_pageleave: true,
        capture_exceptions: true,
        // Replay starts only through syncRecording (a signed-in host, off the auth pages), and
        // masks every input and every piece of page text.
        disable_session_recording: true,
        session_recording: { maskAllInputs: true, maskTextSelector: '*' },
        before_send: scrubEvent,
      });
      // STR Leads shares this PostHog project: every event from this app carries app: social.
      posthog.register({ app: 'social' });
      client = posthog;
      const queued = pending;
      pending = [];
      for (const fn of queued) run(fn);
      return true;
    })
    .catch((err: unknown) => {
      console.warn('[analytics] PostHog did not start', err);
      pending = [];
      starting = null;
      return false;
    });
  return starting;
}

/** The pages that carry a secret or a password form: never recorded. */
const UNRECORDED = [
  '/login',
  '/signup',
  '/onboarding/plan',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/invite',
  '/data-deletion',
  '/mailbox',
];

function unrecorded(pathname: string) {
  return UNRECORDED.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Session replay runs only for a signed-in host and stops on any auth, reset or invite page. */
export function syncRecording(pathname: string, signedIn: boolean) {
  run((ph) => {
    const want = signedIn && !unrecorded(pathname);
    const on = ph.sessionRecordingStarted();
    if (want && !on) ph.startSessionRecording();
    else if (!want && on) ph.stopSessionRecording();
  });
}

/** A URL or path without its query string or fragment, and without an invite's token. */
export function scrubUrl(value: string): string {
  return value.replace(/[?#].*$/s, '').replace(/\/invite\/[^/]+/, '/invite/:token');
}

const URL_KEY = /url|referrer|pathname|href/i;

function scrubProps(props: Record<string, unknown> | undefined) {
  if (!props) return props;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props))
    out[k] = typeof v === 'string' && URL_KEY.test(k) ? scrubUrl(v) : v;
  return out;
}

/** rrweb's Meta event (type 4) carries the page's full href. */
function scrubSnapshots(data: unknown) {
  if (!Array.isArray(data)) return data;
  return data.map((e: unknown) => {
    const ev = e as { type?: unknown; data?: { href?: unknown } } | null;
    if (ev?.type !== 4 || typeof ev.data?.href !== 'string') return e;
    return { ...ev, data: { ...ev.data, href: scrubUrl(ev.data.href) } };
  });
}

/** PostHog's before_send: no query string, fragment or invite token leaves the browser. */
function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const properties = scrubProps(event.properties) ?? {};
  if ('$snapshot_data' in properties)
    properties.$snapshot_data = scrubSnapshots(properties.$snapshot_data);
  return {
    ...event,
    properties,
    ...(event.$set ? { $set: scrubProps(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubProps(event.$set_once) } : {}),
  };
}

/** A client event: snake_case object_verb (composer_opened). */
export function track(event: string, properties?: Properties) {
  run((ph) => ph.capture(event, properties));
}

/** Sends `event` once when the component mounts (a screen or sheet was seen). */
export function useTrackView(event: string, properties?: Properties) {
  const sent = useRef(false);
  const first = useRef(properties);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    track(event, first.current);
  }, [event]);
}

/** Ties this browser's events (and its anonymous history) to the signed-in host. */
export function identifyUser(user: { id: string; email: string }) {
  run((ph) => ph.identify(user.id, { email: user.email }));
}

/** Sign-out: the next visitor on this browser starts as a new anonymous person. */
export function resetAnalytics() {
  run((ph) => ph.reset());
}

export function captureError(error: unknown, properties?: Properties) {
  run((ph) => ph.captureException(error, properties));
}

/**
 * Keeps the UTM tags of the link that brought the visitor (the marketing site's sign-up links)
 * for the sign-up to read. First touch wins for 30 days.
 */
export function rememberAttribution(search: string) {
  try {
    if (!trackingAllowed() || readCookie(ATTRIBUTION_COOKIE)) return;
    const params = new URLSearchParams(search);
    const tags: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const v = params.get(key)?.trim();
      if (v) tags[key] = v.slice(0, 200);
    }
    if (!Object.keys(tags).length) return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(tags))}; Path=/; Max-Age=${ATTRIBUTION_DAYS * 86_400}; SameSite=Lax${secure}`;
  } catch {
    // Attribution is a nice-to-have; never break the page over it.
  }
}
